import { useEffect, useState } from "react";
import {
  getThreadMessages,
  sendThreadMessage,
  unblockUser,
} from "components/messaging-thread/service";
import {
  ThreadDetailsResponse,
  ThreadMessageResponse,
} from "components/messaging-thread/types";
import { generateTempId } from "helpers";
import { useDispatch } from "react-redux";
import {
  refreshThreadsDetails,
  refreshThreadsList,
} from "components/messaging-thread/messagingThreadSlice";

export const useThreadDetailsMessages = (
  thread: ThreadDetailsResponse | null
) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState<ThreadMessageResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [unblocking, setUnblocking] = useState(false);

  const user = JSON.parse(localStorage.getItem("user")!);
  const currentUserId = user?.uid;

  const isBlocked = thread?.blockContext?.isBlocked;
  const blockedByMe = thread?.blockContext?.blockedByMe;
  const blockedMe = thread?.blockContext?.blockedMe;

  useEffect(() => {
    if (!thread?.id) return;

    const fetchMessages = async () => {
      setLoading(true);
      try {
        const data = await getThreadMessages(thread.id);
        setMessages(data);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [thread?.id]);

  const sendMessage = async (body: string) => {
    if (!thread?.id || !body.trim()) return;

    setSending(true);
    try {
      await sendThreadMessage(thread.id, { body });

      setMessages((prev) => [
        ...prev,
        {
          id: generateTempId(),
          senderId: currentUserId,
          type: "text",
          body,
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleUnblock = async () => {
    if (!thread?.participant?.id || !thread?.id) return;

    try {
      setUnblocking(true);
      await unblockUser(thread.participant.id);
      dispatch(refreshThreadsList());
      dispatch(refreshThreadsDetails());
    } catch (error) {
      console.error("Failed to unblock user", error);
    } finally {
      setUnblocking(false);
      setShowUnblockConfirm(false);
    }
  };

  return {
    messages,
    loading,
    sending,
    sendMessage,
    currentUserId,
    user,

    isBlocked,
    blockedByMe,
    blockedMe,

    showUnblockConfirm,
    setShowUnblockConfirm,
    unblocking,
    handleUnblock,
  };
};
